import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Label from "../../label";
import Textbox from "../../form/input";
import { Color } from "../../../constants/color";
import { BreakpointsQuery } from "../../../constants/device";
import { Headquarter, UserPageResponse } from "../../../types";
import Button from "../../button/button";
import Suggerimento from "../../tooltip/suggerimento";
import { Icons } from "../../../constants/icons";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

interface Props {
  show: boolean;
  edit: boolean;
  companyData: UserPageResponse | undefined;
  editCompanyData: (
    field: string,
    event: ChangeEvent<HTMLInputElement>,
    index: number,
    headquarters?: Headquarter[]
  ) => void;
  updateHeadquarters?: (newHeadquarters: Headquarter[]) => void;
}

const MainContainer = styled.div<{
  $show: boolean;
}>`
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
`;

const InfoContainer = styled.div<{
  $edit: boolean;
}>`
  display: flex;
  flex-direction: row;
  column-gap: 80px;
  margin-top: 30px;
  position: ${(props) => (props.$edit ? "relative" : "unset")};
  right: ${(props) => (props.$edit ? "66px" : "unset")};
  input {
    background-color: ${Color.WHITE};
    box-shadow: none;
    border-style: solid;
    border-width: 2px;
    border-color: ${Color.LIGHT_BLUE};
    border-radius: 20px;
    width: 480px;
    height: 30px;
    @media ${BreakpointsQuery.tablet} {
      width: 400px;
    }
  }
  input:focus {
    outline-width: 0;
  }
  @media ${BreakpointsQuery.tablet} {
    column-gap: 20px;
    right: ${(props) => (props.$edit ? "35px" : "unset")};
  }
`;

const Row = styled.div`
  display: flex;
  column-gap: 80px;
  margin-top: 30px;
  input {
    background-color: ${Color.WHITE};
    box-shadow: none;
    border-style: solid;
    border-width: 2px;
    border-color: ${Color.LIGHT_BLUE};
    border-radius: 20px;
    width: 480px;
    height: 30px;
    @media ${BreakpointsQuery.tablet} {
      width: 400px;
    }
  }
  input:focus {
    outline-width: 0;
  }
  @media ${BreakpointsQuery.tablet} {
    column-gap: 20px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 30px;
`;

const RemoveButtonContainer = styled.div<{
  $edit: boolean;
}>`
  display: ${(props) => (props.$edit ? "unset" : "none")};
  margin-top: 24px;
  width: min-content;
  position: relative;
  left: 65px;
  @media ${BreakpointsQuery.tablet} {
    left: 5px;
  }
`;

const ButtonContainer = styled.div<{
  $edit: boolean;
}>`
  display: ${(props) => (props.$edit ? "unset" : "none")};
  margin-top: 30px;
`;

const Headquarters = ({
  show,
  edit,
  companyData,
  editCompanyData,
  updateHeadquarters,
}: Props) => {
  const { t } = useTranslation();
  const [headquarters, setHeadquarters] = useState<
    Array<Headquarter & { tempId?: number }>
  >([]);

  // Un contatore, non l'orologio: con Date.now() più un numero a caso fra 0 e 999, due sedi
  // create nello stesso millisecondo avevano spesso lo stesso tempId. Con 82 sedi React mostrava
  // righe doppie e ne nascondeva altre, e la rimozione, che filtra per tempId, poteva togliere la
  // sede sbagliata.
  const nextTempId = useRef(0);
  const generateTempId = () => {
    nextTempId.current += 1;
    return nextTempId.current;
  };

  useEffect(() => {
    if (companyData?.headquarters) {
      // Aggiungiamo un ID temporaneo a ciascun headquarter
      const headquartersWithId = [...companyData.headquarters].map((hq) => ({
        ...hq,
        tempId: generateTempId(),
      }));
      setHeadquarters(headquartersWithId);
    }
  }, [companyData]);

  return (
    <MainContainer $show={show}>
      <Label
        text={t("user-headquarters-label")}
        fontSize={"26px"}
        fontWeight={"bold"}
        textTransform={"uppercase"}
      />
      <Row>
        <Textbox
          label={t("user-registered-office-address")}
          alt={t("user-registered-office-address")}
          autocomplete={"off"}
          form={"company-info"}
          name={t("user-registered-office-address")}
          defaultValue={
            companyData ? companyData.companyInfo.registeredOfficeAddress : ""
          }
          type={"text"}
          textAlign={"left"}
          onChange={(e) => {
            editCompanyData(`companyInfo.registeredOfficeAddress`, e, -1);
          }}
          required={false}
          edit={edit}
        />
        <Textbox
          label={t("user-registered-office-telephone")}
          alt={t("user-registered-office-telephone")}
          autocomplete={"off"}
          form={"company-info"}
          name={t("user-registered-office-telephone")}
          defaultValue={
            companyData ? companyData.companyInfo.registeredOfficeTelephone : ""
          }
          type={"text"}
          textAlign={"left"}
          onChange={(e) => {
            editCompanyData(`companyInfo.registeredOfficeTelephone`, e, -1);
          }}
          required={false}
          edit={edit}
          maxLength={10}
        />
      </Row>
      {headquarters &&
        headquarters.map((headquarter, index) => {
          return (
            <InfoContainer key={headquarter.tempId || index} $edit={edit}>
              <RemoveButtonContainer $edit={edit}>
                <Suggerimento testo={t("user-aiuto-rimuovi-sede")}>
                  <Button
                    color={Color.RED}
                    textColor={Color.WHITE}
                    aria-label={t("user-rimuovi-sede", {
                      sede: headquarter.headquarterName || index + 1,
                    })}
                    icon={Icons.BUTTONS.CANCEL}
                    onClick={() => {
                      // Elimina l'headquarters basandosi sull'ID temporaneo
                      const updatedHeadquarters = headquarters.filter(
                        (hq) => hq.tempId !== headquarter.tempId
                      );

                      // Aggiorno lo stato locale
                      setHeadquarters(updatedHeadquarters);

                      // Aggiorno anche lo stato nel componente padre
                      // Prima di passare al componente padre, rimuoviamo la proprietà tempId
                      const cleanHeadquarters = updatedHeadquarters.map(
                        ({ tempId, ...rest }) => rest
                      );
                      if (updateHeadquarters) {
                        updateHeadquarters(cleanHeadquarters);
                      }
                    }}
                  />
                </Suggerimento>
              </RemoveButtonContainer>
              <Row style={{ gap: "2.5rem" }}>
                <Textbox
                  label={
                    t("user-registered-headquarter-name") + " " + (index + 1)
                  }
                  alt={t("user-registered-headquarter-name")}
                  autocomplete={"off"}
                  form={"company-info"}
                  name={t("user-registered-headquarter-name")}
                  defaultValue={headquarter.headquarterName}
                  type={"text"}
                  textAlign={"left"}
                  onChange={(e) => {
                    const updatedHeadquarters = [...headquarters];
                    updatedHeadquarters[index].headquarterName = e.target.value;
                    setHeadquarters(updatedHeadquarters);

                    // Creiamo una copia degli headquarters senza il tempId per passarla alla funzione
                    const cleanHeadquarters = updatedHeadquarters.map(
                      ({ tempId, ...rest }) => rest
                    );
                    editCompanyData(
                      `headquarter.headquarterName`,
                      e,
                      index,
                      cleanHeadquarters
                    );
                  }}
                  required={false}
                  edit={edit}
                  style={{ width: "20rem" }}
                />
                <Textbox
                  label={t("user-operating-office-address") + " " + (index + 1)}
                  alt={t("user-operating-office-address")}
                  autocomplete={"off"}
                  form={"company-info"}
                  name={t("user-operating-office-address")}
                  defaultValue={headquarter.operativeOfficeAddress}
                  type={"text"}
                  textAlign={"left"}
                  onChange={(e) => {
                    // Aggiorniamo lo stato locale
                    const updatedHeadquarters = [...headquarters];
                    updatedHeadquarters[index].operativeOfficeAddress =
                      e.target.value;
                    setHeadquarters(updatedHeadquarters);

                    // Creiamo una copia degli headquarters senza il tempId per passarla alla funzione
                    const cleanHeadquarters = updatedHeadquarters.map(
                      ({ tempId, ...rest }) => rest
                    );
                    editCompanyData(
                      `headquarter.operativeOfficeAddress`,
                      e,
                      index,
                      cleanHeadquarters
                    );
                  }}
                  required={false}
                  edit={edit}
                  style={{ width: "20rem" }}
                />
                <Textbox
                  label={
                    t("user-operating-office-telephone") + " " + (index + 1)
                  }
                  alt={t("user-operating-office-telephone")}
                  autocomplete={"off"}
                  form={"company-info"}
                  name={t("user-operating-office-telephone")}
                  defaultValue={headquarter.operativeOfficeTelephone}
                  type={"text"}
                  textAlign={"left"}
                  onChange={(e) => {
                    // Aggiorniamo lo stato locale
                    const updatedHeadquarters = [...headquarters];
                    updatedHeadquarters[index].operativeOfficeTelephone =
                      e.target.value;
                    setHeadquarters(updatedHeadquarters);

                    // Creiamo una copia degli headquarters senza il tempId per passarla alla funzione
                    const cleanHeadquarters = updatedHeadquarters.map(
                      ({ tempId, ...rest }) => rest
                    );
                    if (e.target.value.length <= 10) {
                      editCompanyData(
                        `headquarter.operativeOfficeTelephone`,
                        e,
                        index,
                        cleanHeadquarters
                      );
                    }
                  }}
                  required={false}
                  edit={edit}
                  style={{ width: "20rem" }}
                  maxLength={10}
                />
              </Row>
            </InfoContainer>
          );
        })}
      <ButtonContainer $edit={edit}>
        <Suggerimento testo={t("user-aiuto-aggiungi-sede")}>
          <Button
            color={Color.LIGHT_BLUE}
            textColor={Color.WHITE}
            icon={Icons.BUTTONS.PLUS}
            label={t("user-add-operative-office")}
            onClick={() => {
              // Creo un nuovo headquarter con ID temporaneo
              const newHeadquarter = {
                headquarterName: "",
                operativeOfficeAddress: "",
                operativeOfficeTelephone: "",
                tempId: generateTempId(),
              };

              // Creo un nuovo array con tutti gli headquarters esistenti più quello nuovo
              const updatedHeadquarters = [...headquarters, newHeadquarter];

              // Aggiorno lo stato locale
              setHeadquarters(updatedHeadquarters);

              // Aggiorno anche lo stato nel componente padre
              // Prima di passare al componente padre, rimuoviamo la proprietà tempId
              const cleanHeadquarters = updatedHeadquarters.map(
                ({ tempId, ...rest }) => rest
              );
              if (updateHeadquarters) {
                updateHeadquarters(cleanHeadquarters);
              }
            }}
          />
        </Suggerimento>
      </ButtonContainer>
    </MainContainer>
  );
};

export default Headquarters;
